import { AggregateFunc, createQuery, TypedQueryPayload, ValidPath } from "@nova/queryable"
import { DistanceUnit, formatAltitude, formatDistance, formatSpeed } from "@nova/format"
import { usePilotTrackingQuery } from "features/pilot-tracking/PilotTrackingQueries"
import { useMemo, useState } from "react"
import { CardCollection } from "features/pilot-tracking/CardCollection"
import classNames from "classnames"
import { ExpandableContent } from "@nova/component-expandable-content"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { ProfilePhoto } from "features/data-management/workspace/workspace/body/settings-and-collaborators/profile/ProfilePhoto"
import { useRouter } from "pages"
import { useAppSelector } from "integrations/redux/hooks"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectAllRegisteredMembers } from "features/entity-caching/members/UserManagementSlice"

const pilotTrackingRecordData = {
    fastestSpeed: {
        title: "Fastest Speed",
        imageSrc: "/images/pilot-tracking-records/fastest-speed.webp",
        query: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "max_speed", func: "MAX", alias: "value" }],
            orderBy: [{ field: "value", dir: "DESC" }],
            limit: 10,
        }),
    },
    longestDistance: {
        title: "Longest Distance",
        imageSrc: "/images/pilot-tracking-records/longest-distance.webp",
        query: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "distance_flown", func: "MAX", alias: "value" }],
            orderBy: [{ field: "value", dir: "DESC" }],
            limit: 10,
        }),
    },
    longestFlightTime: {
        title: "Longest Flight Time",
        imageSrc: "/images/pilot-tracking-records/longest-flight-time.webp",
        query: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "airtime", func: "MAX", alias: "value" }],
            orderBy: [{ field: "value", dir: "DESC" }],
            limit: 10,
        }),
    },
    highestAltitude: {
        title: "Highest Altitude",
        imageSrc: "/images/pilot-tracking-records/highest-altitude.webp",
        query: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "max_altitude", func: "MAX", alias: "value" }],
            orderBy: [{ field: "value", dir: "DESC" }],
            limit: 10,
        }),
    },
    milesCovered: {
        title: "Miles Covered",
        imageSrc: "/images/pilot-tracking-records/miles-covered.webp",
        query: createQuery({
            resource: "metadata",
            select: ["mission.user_id"],
            aggregates: [{ field: "distance_flown", func: "SUM", alias: "value" }],
            orderBy: [{ field: "value", dir: "DESC" }],
            limit: 10,
        }),
    },
}

function getRecordDisplayValue(
    record: PilotTrackingRecord,
    value: number,
    distanceUnit: DistanceUnit
): string {
    switch (record) {
        case "fastestSpeed":
            return formatSpeed({ value, unit: distanceUnit })
        case "longestDistance":
        case "milesCovered":
            return formatDistance({ value, unit: distanceUnit })
        case "longestFlightTime":
            return `${Math.round(value / 60)} minutes`
        case "highestAltitude":
            return formatAltitude({ value, unit: distanceUnit })
    }
}
type PilotTrackingRecord = keyof typeof pilotTrackingRecordData
const pilotTrackingRecords = Object.keys(pilotTrackingRecordData) as PilotTrackingRecord[]
type PilotRecordQuery = TypedQueryPayload<
    "metadata",
    readonly ["mission.user_id"],
    readonly [{ field: ValidPath<"metadata">; func: AggregateFunc; alias: "value" }],
    undefined
>

export function RecordsSection() {
    return (
        <div className="flex flex-col gap-3">
            <div className="text-gray-50 text-[22px] telegraf">Records</div>
            <CardCollection
                cards={pilotTrackingRecords.map((r) => (
                    <Record key={r} record={r} />
                ))}
                orientation="vertical"
            />
        </div>
    )
}

function Record({ record }: { record: PilotTrackingRecord }) {
    const router = useRouter("/workspace/:workspaceId")
    const [isOpen, setIsOpen] = useState(false)
    const recordData = useMemo(() => pilotTrackingRecordData[record], [record])
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)
    const { data: rawData } = usePilotTrackingQuery(recordData.query as PilotRecordQuery)
    const data = rawData as { "mission.user_id": string | null; value: number | null }[] | undefined
    const users = useAppSelector(selectAllRegisteredMembers)
    const richData = useMemo(() => {
        return (
            data?.map((d) => {
                const user = users?.find((u) => u.id === d["mission.user_id"])
                const displayValue =
                    d.value !== null ? getRecordDisplayValue(record, d.value, distanceUnit) : "--"
                return {
                    userId: d["mission.user_id"],
                    userFullName: user?.fullName ?? "Unknown User",
                    value: d.value,
                    displayValue,
                }
            }) ?? []
        )
    }, [data, distanceUnit, record, users])
    const winner = !!richData.length ? richData[0] : null
    return (
        <div className="w-full">
            <div
                className="flex gap-3 w-full px-4 py-3 group hover:bg-white-100 transition-colors select-none cursor-pointer"
                onClick={() => setIsOpen((p) => !p)}
            >
                <div className="w-[76px] min-w-[76px] max-w-[76px] h-[76px] min-h-[76px] max-h-[76px] overflow-hidden rounded-lg flex justify-center items-center relative p-0.5">
                    <PilotTrackingRecordImage record={record} isOpen={isOpen} />
                </div>
                <div className="flex-1 flex items-center h-fit gap-2">
                    <div
                        className={classNames(
                            "text-2xl telegraf transition-colors w-[240px] shrink-0",
                            {
                                "text-gray-100 group-hover:text-white": !isOpen,
                                "text-white": isOpen,
                            }
                        )}
                    >
                        {recordData.title}
                    </div>
                    <router.Link
                        to={{
                            path: "/fleet/pilots/:pilotId",
                            params: { pilotId: winner?.userId ?? "" },
                        }}
                    >
                        <div className="font-mono text-gray-50 hover:text-blue-500 hover:underline">
                            {winner?.userFullName ?? "--"}
                        </div>
                    </router.Link>
                    <div className="font-mono text-gray-100 grow shrink-0 text-right">
                        {winner?.displayValue ?? "--"}
                    </div>
                </div>
            </div>
            <ExpandableContent isOpen={isOpen}>
                <div className="pl-[156px]">
                    <ScrollableArea horizontal>
                        <div className="flex gap-6 py-8">
                            {richData.map((d, i) => (
                                <RecordUser key={d.userId ?? i} data={d} placement={i + 1} />
                            ))}
                            <div className="w-6 shrink-0">{/* Spacer */}</div>
                        </div>
                    </ScrollableArea>
                </div>
            </ExpandableContent>
        </div>
    )
}

const getReadablePlacement = (placement: number) => {
    if (placement === 1) return "First Place"
    if (placement === 2) return "Second Place"
    if (placement === 3) return "Third Place"
}
function RecordUser({
    data,
    placement,
}: {
    data: {
        userId: string | null
        userFullName: string
        value: number | null
        displayValue: string
    }
    placement: number
}) {
    const router = useRouter("/workspace/:workspaceId")
    return (
        <div className="flex flex-col gap-5">
            <div className="w-[136px] shrink-0 grow-0 aspect-square overflow-hidden rounded-2xl relative">
                <ProfilePhoto
                    userId={data.userId ?? ""}
                    fullName={data.userFullName}
                    initialsPlacement="bottom-left"
                    initialsFontSize={20}
                    initialsFont="mono"
                />
                <PlacementOverlay place={placement} className="rounded-2xl" />
            </div>
            <div className="flex flex-col gap-3">
                <div className="h-[22px]">
                    {placement <= 3 && (
                        <img
                            src={`/images/pilot-tracking-records/placement-${placement}.webp`}
                            alt={getReadablePlacement(placement)}
                            className="w-[38px]"
                        />
                    )}
                    {placement > 3 && <div className="font-mono text-[16px]">{placement}</div>}
                </div>
                <router.Link
                    to={{ path: "/fleet/pilots/:pilotId", params: { pilotId: data.userId ?? "" } }}
                >
                    <div className="font-mono text-white hover:text-blue-500 hover:underline">
                        {data.userFullName}
                    </div>
                </router.Link>
                <small className="xs text-gray-100">{data.displayValue}</small>
            </div>
        </div>
    )
}

const placementOverlayStyles = [
    {
        background:
            "linear-gradient(180deg, rgba(128, 118, 0, 0.00) 0%, rgba(255, 234, 0, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
    {
        background:
            "linear-gradient(180deg, rgba(50, 50, 50, 0.40) 0%, rgba(184, 184, 184, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
    {
        background:
            "linear-gradient(180deg, rgba(99, 46, 0, 0.40) 0%, rgba(255, 119, 0, 0.40) 100%)",
        boxShadow: "inset 0 0 0 3px rgba(255, 255, 255, 0.2)",
    },
]

function PlacementOverlay({ place, className }: { place: number; className?: string }) {
    if (place > 3 || place < 1) return null
    return (
        <div
            className={classNames("absolute inset-0", className)}
            style={placementOverlayStyles[place - 1]}
        />
    )
}

function PilotTrackingRecordImage({
    record,
    isOpen,
}: {
    record: PilotTrackingRecord
    isOpen: boolean
}) {
    const recordData = useMemo(() => pilotTrackingRecordData[record], [record])
    const [hasLoadedBorder, setHasLoadedBorder] = useState(false)
    const [hasLoadedImage, setHasLoadedImage] = useState(false)
    return (
        <div
            className={classNames("w-full h-full", {
                "opacity-0": !hasLoadedBorder || !hasLoadedImage,
            })}
        >
            <img
                src="/images/pilot-tracking-records/open-record-border.webp"
                alt=""
                className={classNames("absolute inset-0 w-full h-full transition-transform", {
                    "scale-90": !isOpen,
                    "scale-100": isOpen,
                })}
                onLoad={() => setHasLoadedBorder(true)}
                onError={() => setHasLoadedBorder(true)}
            />
            <div
                className={classNames(
                    "w-full h-full overflow-hidden rounded-lg border transition-all",
                    {
                        "border-white-100 group-hover:border-white-500 grayscale group-hover:grayscale-0 brightness-50 group-hover:brightness-100":
                            !isOpen,
                        "border-transparent": isOpen,
                    }
                )}
            >
                <div className="w-full h-full relative flex justify-center items-center">
                    <img
                        key={record}
                        src={recordData.imageSrc}
                        alt={recordData.title}
                        className="absolute inset-0 w-full h-full object-cover"
                        onLoad={() => setHasLoadedImage(true)}
                    />
                </div>
            </div>
        </div>
    )
}
